// src/composables/useDealerLocations.js
import { ref } from 'vue'

export function useDealerLocations(apiBase = '/wp-json/dealers/v1/locations') {
  const dealersLocations = ref([])
  const loading = ref(false)
  const error = ref(null)

  const fetchDealerLocations = async (params = {}) => {
    const { search = '', userLocation = null, radius = 0, products = [], enableFallback = false, enableMixedSearch = false } = params;

    
    // Prevent duplicate requests if already loading
    if (loading.value) {
      console.warn('Skipping duplicate API request - already loading');
      return;
    }
    
    // Don't make request if radius is specified but no user location
    if (radius > 0 && (!userLocation || !userLocation.lat || !userLocation.lng)) {
      console.warn('User location is required for radius-based search');
      error.value = 'User location is required for radius-based search';
      return;
    }

    // Don't make request if no search context is provided (no search term, no user location)
    if (!search && (!userLocation || !userLocation.lat || !userLocation.lng) && radius === 0) {
      console.warn('No search context provided - search term or user location required');
      error.value = 'Please provide a search location or allow location access to find dealers';
      return;
    }

    loading.value = true
    error.value = null

    try {
      const queryParams = new URLSearchParams();
      
      if (search) {
        queryParams.append('search', search);
      }
      
      if (userLocation && userLocation.lat && userLocation.lng) {
        queryParams.append('lat', userLocation.lat.toString());
        queryParams.append('lng', userLocation.lng.toString());
      }
      
      if (radius > 0) {
        queryParams.append('radius', radius.toString());
      }
      
      // Add products filter to the query
      if (products && products.length > 0) {
        products.forEach(product => {
          queryParams.append('products[]', product);
        });
      }

      // Add fallback parameter when enabled
      if (enableFallback) {
        queryParams.append('fallback_to_online_retailers', 'true');
      }

      // Add mixed search parameter when enabled
      if (enableMixedSearch) {
        queryParams.append('enable_mixed_search', 'true');
      }

      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${apiBase}${query}`;
      

      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch dealer locations');
      }

      const data = await response.json()
      
      // Handle mixed search response structure
      if (data && typeof data === 'object' && data.search_type === 'mixed') {
        dealersLocations.value = data.results

        
        // Return enhanced metadata for mixed search
        return {
          results: data.results,
          searchType: 'mixed',
          localProducts: data.local_products || [],
          onlineProducts: data.online_products || [],
          localCount: data.local_count || 0,
          onlineCount: data.online_count || 0,
          wasFiltered: products.length > 0,
          hadRadius: radius > 0,
          usedFallback: enableFallback || enableMixedSearch
        };
      } else {
        // Handle regular response
        const results = Array.isArray(data) ? data : [];
        dealersLocations.value = results

        
        // Return standard metadata
        return {
          results: results,
          searchType: 'standard',
          wasFiltered: products.length > 0,
          hadRadius: radius > 0,
          usedFallback: enableFallback && results.length > 0 && results.some(dealer => dealer.onlineRetailer)
        };
      }
    } catch (err) {
      error.value = err.message
      dealersLocations.value = []
      return {
        results: [],
        searchType: 'error',
        wasFiltered: products.length > 0,
        hadRadius: radius > 0,
        usedFallback: false
      };
    } finally {
      loading.value = false
    }
  }

  return {
    dealersLocations,
    loading,
    error,
    fetchDealerLocations,
  }
}